(function () {
  'use strict';

  var DESIGN_MODE = !!(window.Shopify && window.Shopify.designMode);
  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var FINE = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  function qsa(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function initHeader() {
    var header = document.querySelector('[data-v3-header]');
    if (!header || header.dataset.v3Ready === '1') return;
    header.dataset.v3Ready = '1';

    function update() {
      header.classList.toggle('is-scrolled', window.scrollY > 20);
    }
    update();
    window.addEventListener('scroll', update, { passive: true });
  }

  function initReveal(root) {
    var items = qsa('[data-v3-reveal]:not([data-v3-reveal-ready])', root);
    if (!items.length) return;

    items.forEach(function (el) { el.setAttribute('data-v3-reveal-ready', '1'); });

    if (REDUCED || !('IntersectionObserver' in window)) {
      items.forEach(function (el) { el.classList.add('is-v3-visible'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-v3-visible');
        io.unobserve(entry.target);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -4% 0px' });

    items.forEach(function (el) { io.observe(el); });
  }

  function initParallax(root) {
    if (!FINE || REDUCED || DESIGN_MODE) return;

    qsa('[data-v3-parallax-root]:not([data-v3-parallax-ready])', root).forEach(function (stage) {
      stage.setAttribute('data-v3-parallax-ready', '1');
      var layers = qsa('[data-v3-depth]', stage);
      if (!layers.length) return;

      var targetX = 0, targetY = 0, currentX = 0, currentY = 0, raf = null;

      function render() {
        currentX += (targetX - currentX) * 0.12;
        currentY += (targetY - currentY) * 0.12;
        layers.forEach(function (layer) {
          var depth = parseFloat(layer.getAttribute('data-v3-depth')) || 1;
          layer.style.setProperty('--v3-x', (currentX * depth).toFixed(2) + 'px');
          layer.style.setProperty('--v3-y', (currentY * depth).toFixed(2) + 'px');
        });
        if (Math.abs(targetX - currentX) > 0.05 || Math.abs(targetY - currentY) > 0.05) {
          raf = requestAnimationFrame(render);
        } else {
          raf = null;
        }
      }

      function kick() {
        if (!raf) raf = requestAnimationFrame(render);
      }

      stage.addEventListener('pointermove', function (event) {
        var rect = stage.getBoundingClientRect();
        var nx = ((event.clientX - rect.left) / rect.width) - 0.5;
        var ny = ((event.clientY - rect.top) / rect.height) - 0.5;
        targetX = nx * 16;
        targetY = ny * 12;
        kick();
      }, { passive: true });

      stage.addEventListener('pointerleave', function () {
        targetX = 0;
        targetY = 0;
        kick();
      });
    });
  }

  function initContents(root) {
    qsa('[data-v3-contents]:not([data-v3-contents-ready])', root).forEach(function (section) {
      section.setAttribute('data-v3-contents-ready', '1');
      var triggers = qsa('[data-v3-content-trigger]', section);
      var panels = qsa('[data-v3-content-panel]', section);
      if (!triggers.length || !panels.length) return;

      function activate(id) {
        triggers.forEach(function (btn) {
          var active = btn.getAttribute('data-v3-content-trigger') === id;
          btn.classList.toggle('is-active', active);
          btn.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        panels.forEach(function (panel) {
          var active = panel.getAttribute('data-v3-content-panel') === id;
          panel.classList.toggle('is-active', active);
          panel.setAttribute('aria-hidden', active ? 'false' : 'true');
        });
      }

      triggers.forEach(function (btn) {
        var id = btn.getAttribute('data-v3-content-trigger');
        btn.addEventListener('click', function () { activate(id); });
        btn.addEventListener('focus', function () { activate(id); });
        if (FINE) btn.addEventListener('mouseenter', function () { activate(id); });
      });
    });
  }

  function initJourney(root) {
    qsa('[data-v3-journey]:not([data-v3-journey-ready])', root).forEach(function (section) {
      section.setAttribute('data-v3-journey-ready', '1');
      var steps = qsa('[data-v3-journey-step]', section);
      var word = section.querySelector('[data-v3-journey-word]');
      var meter = section.querySelector('[data-v3-journey-meter]');
      if (!steps.length) return;

      function activate(step, index) {
        steps.forEach(function (el) { el.classList.toggle('is-active', el === step); });
        if (meter) meter.style.width = (((index + 1) / steps.length) * 100) + '%';
        if (word) {
          var next = step.getAttribute('data-step-title') || '';
          if (word.textContent.trim() !== next) {
            word.classList.add('is-changing');
            window.setTimeout(function () {
              word.textContent = next;
              word.classList.remove('is-changing');
            }, REDUCED ? 0 : 150);
          }
        }
      }

      if (REDUCED || !('IntersectionObserver' in window)) {
        activate(steps[0], 0);
        return;
      }

      var io = new IntersectionObserver(function (entries) {
        var visible = entries.filter(function (entry) { return entry.isIntersecting; });
        if (!visible.length) return;
        visible.sort(function (a, b) { return b.intersectionRatio - a.intersectionRatio; });
        var step = visible[0].target;
        activate(step, steps.indexOf(step));
      }, { threshold: [0.25, 0.45, 0.65], rootMargin: '-18% 0px -38% 0px' });

      steps.forEach(function (step) { io.observe(step); });
    });
  }

  function initTilt(root) {
    if (!FINE || REDUCED || DESIGN_MODE) return;
    qsa('[data-v3-tilt]:not([data-v3-tilt-ready])', root).forEach(function (card) {
      card.setAttribute('data-v3-tilt-ready', '1');
      card.addEventListener('pointermove', function (event) {
        var rect = card.getBoundingClientRect();
        var x = ((event.clientX - rect.left) / rect.width) - 0.5;
        var y = ((event.clientY - rect.top) / rect.height) - 0.5;
        card.style.transform = 'perspective(800px) rotateX(' + (-y * 3).toFixed(2) + 'deg) rotateY(' + (x * 3).toFixed(2) + 'deg) translateY(-3px)';
      }, { passive: true });
      card.addEventListener('pointerleave', function () { card.style.transform = ''; });
    });
  }

  function initPageTransitions() {
    if (DESIGN_MODE || REDUCED || !document.body.classList.contains('v3-transitions-enabled')) return;
    var overlay = document.querySelector('[data-v3-page-transition]');
    if (!overlay || overlay.dataset.v3Ready === '1') return;
    overlay.dataset.v3Ready = '1';

    document.addEventListener('click', function (event) {
      var link = event.target.closest('a[href]');
      if (!link) return;
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (link.target && link.target !== '_self') return;
      if (link.hasAttribute('download')) return;

      var href = link.getAttribute('href');
      if (!href || href.charAt(0) === '#' || href.indexOf('mailto:') === 0 || href.indexOf('tel:') === 0) return;

      var url;
      try { url = new URL(link.href, window.location.href); } catch (e) { return; }
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash) return;

      event.preventDefault();
      overlay.classList.add('is-leaving');
      window.setTimeout(function () { window.location.href = url.href; }, 330);
    });
  }

  window.addEventListener('pageshow', function () { var overlay = document.querySelector('[data-v3-page-transition]'); if (overlay) overlay.classList.remove('is-leaving'); });

  function boot(root) {
    initHeader();
    initReveal(root);
    initParallax(root);
    initContents(root);
    // V5 journey owns its pinned scroll and image state.
    initTilt(root);
    initPageTransitions();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }

  document.addEventListener('shopify:section:load', function (event) { boot(event.target); });
})();
